import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import Header from '../components/Header';
import SEOHead, { localBusinessSchema, breadcrumbSchema } from '../components/SEOHead';
import { PhotoContentSections } from '../components/ContentSections';
import { Reveal, Stagger, StaggerItem } from '../components/motion/Reveal';

import AdminLoginModal from '../components/AdminLoginModal';
const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to validate logo URLs
const isValidLogoUrl = (url) => {
  if (!url) return false;
  return url.includes('cloudfront.net') || 
         url.includes('amazonaws.com') || 
         url.includes('crystalmedia') ||
         url.startsWith('/');
};

// Default Crystal Logo URL
const DEFAULT_LOGO_URL = '';

const PhotoPage = () => {
  const [visibleElements, setVisibleElements] = useState(new Set());
  const [collections, setCollections] = useState([]);
  const [activeCollection, setActiveCollection] = useState(null);
  const [settings, setSettings] = useState(null);
  const [editorial, setEditorial] = useState({});
  const [logoUrl, setLogoUrl] = useState('');
  const [logoError, setLogoError] = useState(false);
  const [photoSettings, setPhotoSettings] = useState({
    frame_enabled: true,
    frame_color: '#000000',
    frame_stroke_width: 2,
    spotlight_enabled: true,
    shadow_enabled: true,
    shadow_strength: 50,
    shadow_blur: 40
  });
  const [photoPageContent, setPhotoPageContent] = useState({
    hero_image_url: '',
    hero_heading: '',
    hero_subheading: '',
    hero_focal_desktop: null,
    hero_focal_mobile: null,
    cta_bg_image_url: '',
    cta_overlay_color: '#000000',
    cta_overlay_opacity: 0.4,
    cta_focal_desktop: null,
    cta_focal_mobile: null,
  });
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [descriptionFont, setDescriptionFont] = useState('playfair');
  const [loading, setLoading] = useState(true);
  const [showLoginModal, setShowLoginModal] = useState(false);
  
  // Horizontal scroll state
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isGalleryComplete, setIsGalleryComplete] = useState(false);
  const [showHeader, setShowHeader] = useState(false);
  const galleryRef = useRef(null);
  const galleryContainerRef = useRef(null);
  const observerRef = useRef(null);

  // Track viewport size for focal points
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch data from backend
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [collectionsRes, settingsRes, editorialRes, photoSettingsRes, galleryRes, photoPageContentRes] = await Promise.all([
          fetch(`${API_URL}/api/content/collections`),
          fetch(`${API_URL}/api/content/settings`),
          fetch(`${API_URL}/api/content/editorial`),
          fetch(`${API_URL}/api/content/photo-settings`).catch(() => ({ json: () => ({}) })),
          fetch(`${API_URL}/api/content/photo-gallery`).catch(() => ({ json: () => ({ items: [] }) })),
          fetch(`${API_URL}/api/content/photo-page-content`).catch(() => ({ json: () => ({}) }))
        ]);

        const collectionsData = await collectionsRes.json();
        const settingsData = await settingsRes.json();
        const editorialData = await editorialRes.json();
        const photoSettingsData = await photoSettingsRes.json();
        const galleryData = await galleryRes.json();
        const photoPageContentData = await photoPageContentRes.json();

        // Create a map of all collections
        const collectionsMap = {};
        collectionsData.forEach(c => {
          collectionsMap[c.id] = {
            id: c.id,
            couple: c.couple,
            location: c.location,
            coverImage: c.cover_image_url,
            coverFocalPoint: c.cover_focal_point || { x: 50, y: 50 },
            imageCount: Array.isArray(c.images) ? c.images.length : 0
          };
        });

        // If gallery has items, use those in order; otherwise fall back to all collections
        let displayCollections = [];
        if (galleryData.items && galleryData.items.length > 0) {
          displayCollections = galleryData.items
            .map(item => collectionsMap[item.collection_id])
            .filter(c => c && c.coverImage);
        }
        
        if (displayCollections.length === 0) {
          displayCollections = collectionsData
            .filter(c => c.cover_image_url)
            .map(c => ({
              id: c.id,
              couple: c.couple,
              location: c.location,
              coverImage: c.cover_image_url,
              coverFocalPoint: c.cover_focal_point || { x: 50, y: 50 },
              imageCount: Array.isArray(c.images) ? c.images.length : 0
            }));
        }
        
        setCollections(displayCollections);

        setSettings(settingsData);
        
        // Use photo-specific logo, fallback to general logo - validate URL
        const photoLogo = settingsData.photo_logo_url || settingsData.logo_url || '';
        setLogoUrl(isValidLogoUrl(photoLogo) ? photoLogo : '');

        // Set description font
        if (settingsData.description_font) {
          setDescriptionFont(settingsData.description_font);
        }
        
        if (photoSettingsData && Object.keys(photoSettingsData).length > 0) {
          setPhotoSettings(prev => ({ ...prev, ...photoSettingsData }));
        }

        if (photoPageContentData && Object.keys(photoPageContentData).length > 0) {
          setPhotoPageContent(prev => ({ ...prev, ...photoPageContentData }));
        }
        
        const editorialMap = {};
        editorialData.forEach(section => {
          editorialMap[section.section_name] = section;
        });
        setEditorial(editorialMap);

      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Horizontal scroll handling
  useEffect(() => {
    if (loading || collections.length === 0) return;

    const handleScroll = () => {
      if (!galleryContainerRef.current || !galleryRef.current) return;
      
      const container = galleryContainerRef.current;
      const gallery = galleryRef.current;
      const rect = container.getBoundingClientRect();
      const containerTop = rect.top;
      
      // Calculate how much we've scrolled into the gallery section
      const scrollableWidth = gallery.scrollWidth - window.innerWidth;
      const scrollZoneHeight = Math.max(scrollableWidth, 500); // Minimum scroll zone
      
      // Debug log
      console.log('Scroll:', { containerTop, scrollableWidth, scrollZoneHeight });
      
      if (containerTop > 0) {
        // Before gallery (at top of page) - page just loaded
        setScrollProgress(0);
        setIsGalleryComplete(false);
        setShowHeader(false);
        gallery.style.transform = 'translateX(0)';
      } else if (containerTop <= 0 && containerTop > -scrollZoneHeight) {
        // We're in the horizontal scroll zone
        const progress = Math.abs(containerTop) / scrollZoneHeight;
        setScrollProgress(Math.min(progress, 1));
        setIsGalleryComplete(false);
        setShowHeader(false);
        
        // Apply horizontal transform
        gallery.style.transform = `translateX(-${progress * scrollableWidth}px)`;
      } else {
        // Gallery scroll complete
        setScrollProgress(1);
        setIsGalleryComplete(true);
        setShowHeader(true);
        gallery.style.transform = `translateX(-${scrollableWidth}px)`;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    
    // Delay initial call to ensure DOM is ready
    setTimeout(() => handleScroll(), 100);
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, [loading, collections.length]);

  // Intersection observer for animations
  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleElements((prev) => new Set([...prev, entry.target.id]));
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('[data-animate]').forEach((el) => {
      observerRef.current.observe(el);
    });

    return () => observerRef.current?.disconnect();
  }, [loading]);

  const isVisible = (id) => visibleElements.has(id);

  // Calculate scroll zone height for the sticky gallery
  const scrollZoneHeight = collections.length > 0 
    ? Math.max(collections.length * 300, window.innerWidth || 1920) 
    : 0;

  if (loading) {
    return (
      <main className="bg-white min-h-screen flex items-center justify-center">
        <div className="text-black/30 tracking-widest text-sm uppercase">Loading Gallery...</div>
      </main>
    );
  }

  return (
    <main className="bg-white">
      <SEOHead
        title="Luxury Wedding Photography — Editorial & Timeless"
        description="Crystal Media is a luxury wedding photographer specializing in editorial wedding photography for Pakistani, Indian, Punjabi, Sikh, Muslim, Hindu, and multicultural weddings in Toronto, Vancouver, Montreal, Houston, Miami, Texas, and across Canada and the USA. Timeless images crafted with precision and intention."
        url="/photo"
        keywords={['Luxury Wedding Photography', 'Wedding Photographer Toronto', 'South Asian Wedding Photography', 'Editorial Wedding Photos Canada']}
        siteSettings={settings || {}}
        jsonLd={[localBusinessSchema, breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Photography', path: '/photo' }])]}
      />
      {/* Styles */}
      <style>{`
        .menu-overlay {
          clip-path: polygon(100% 0, 100% 0, 100% 100%, 100% 100%);
          transition: clip-path 0.8s cubic-bezier(0.77, 0, 0.175, 1);
        }
        .menu-overlay.open {
          clip-path: polygon(0 0, 100% 0, 100% 100%, 0 100%);
        }
        .menu-item {
          opacity: 0;
          transform: translateX(60px);
          transition: all 0.6s cubic-bezier(0.77, 0, 0.175, 1);
        }
        .menu-overlay.open .menu-item {
          opacity: 1;
          transform: translateX(0);
        }
        .menu-overlay.open .menu-item:nth-child(1) { transition-delay: 0.4s; }
        .menu-overlay.open .menu-item:nth-child(2) { transition-delay: 0.45s; }
        .menu-overlay.open .menu-item:nth-child(3) { transition-delay: 0.5s; }
        .menu-overlay.open .menu-item:nth-child(4) { transition-delay: 0.55s; }
        .menu-overlay.open .menu-item:nth-child(5) { transition-delay: 0.6s; }
        .menu-overlay.open .menu-item:nth-child(6) { transition-delay: 0.65s; }
        .menu-overlay.open .menu-item:nth-child(7) { transition-delay: 0.7s; }
        .menu-close-btn {
          opacity: 0;
          transform: scale(0.8);
          transition: all 0.4s cubic-bezier(0.77, 0, 0.175, 1) 0.3s;
        }
        .menu-overlay.open .menu-close-btn {
          opacity: 1;
          transform: scale(1);
        }
        .logo-btn {
          transition: transform 0.3s ease, opacity 0.3s ease;
        }
        .logo-btn:hover {
          transform: scale(1.08);
          opacity: 0.8;
        }
        .gallery-frame {
          position: relative;
          transition: transform 0.4s ease, box-shadow 0.4s ease;
        }
        .gallery-frame:hover {
          transform: translateY(-8px);
        }
        .spotlight-container {
          position: relative;
        }
        .spotlight-container::before {
          content: '';
          position: absolute;
          top: -30px;
          left: 50%;
          transform: translateX(-50%);
          width: 120%;
          height: 30px;
          background: radial-gradient(ellipse at center bottom, rgba(255,255,255,0.8) 0%, transparent 70%);
          pointer-events: none;
        }
        .spotlight-glow {
          box-shadow: 
            0 -15px 40px -10px rgba(255,255,255,0.5),
            0 25px 50px -12px rgba(0,0,0,0.15),
            0 4px 6px -1px rgba(0,0,0,0.05);
        }
        .scroll-hint {
          animation: scrollHint 2s ease-in-out infinite;
        }
        @keyframes scrollHint {
          0%, 100% { transform: translateX(0); opacity: 0.5; }
          50% { transform: translateX(10px); opacity: 1; }
        }
        .scroll-hint-arrow {
          animation: scrollHintArrow 1.5s ease-in-out infinite;
        }
        @keyframes scrollHintArrow {
          0%, 100% { transform: translateX(0); opacity: 0.4; }
          50% { transform: translateX(5px); opacity: 1; }
        }
        .scroll-hint-text {
          animation: scrollHintText 1.5s ease-in-out infinite;
        }
        @keyframes scrollHintText {
          0%, 100% { opacity: 0.5; }
          50% { opacity: 1; }
        }
        /* Artistic section backgrounds */
        .section-artistic {
          position: relative;
        }
        .section-artistic::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: 
            radial-gradient(ellipse at 0% 0%, rgba(0,0,0,0.02) 0%, transparent 50%),
            radial-gradient(ellipse at 100% 100%, rgba(0,0,0,0.015) 0%, transparent 50%);
          pointer-events: none;
        }
        .artistic-shadow {
          box-shadow: 
            0 50px 100px -20px rgba(0,0,0,0.08),
            0 30px 60px -30px rgba(0,0,0,0.12);
        }
        .artistic-border {
          position: relative;
        }
        .artistic-border::after {
          content: '';
          position: absolute;
          top: -1px;
          left: 10%;
          right: 10%;
          height: 1px;
          background: linear-gradient(90deg, transparent, rgba(0,0,0,0.08), transparent);
        }
        .floating-inquire {
          animation: floatInquire 3s ease-in-out infinite;
        }
        @keyframes floatInquire {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-5px); }
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>

      {/* Shared Header - Desktop nav, mobile burger menu */}
      <Header logoUrl={logoUrl} variant="light" />

      {/* HERO: Full-width Photo Page Hero */}
      {photoPageContent.hero_image_url && (
        <section className="relative w-full h-[60vh] md:h-[75vh] overflow-hidden" data-testid="photo-hero-section">
          <img
            src={photoPageContent.hero_image_url}
            alt={photoPageContent.hero_heading}
            className="absolute inset-0 w-full h-full object-cover"
            style={{
              objectPosition: (() => {
                const fp = isMobile
                  ? (photoPageContent.hero_focal_mobile || { x: 50, y: 50 })
                  : (photoPageContent.hero_focal_desktop || { x: 50, y: 50 });
                return `${fp.x}% ${fp.y}%`;
              })()
            }}
          />
          <div className="absolute inset-0 bg-black/30" />
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
            <h1 className="font-presti text-4xl sm:text-5xl lg:text-6xl text-white mb-4 drop-shadow-lg" data-testid="photo-hero-heading">
              {photoPageContent.hero_heading}
            </h1>
            {photoPageContent.hero_subheading && (
              <p className="text-white/80 text-base md:text-lg max-w-xl drop-shadow-md" data-testid="photo-hero-subheading">
                {photoPageContent.hero_subheading}
              </p>
            )}
          </div>
        </section>
      )}

      {/* Gallery Section - Grid on Desktop, Horizontal Scroll on Mobile */}
      <div className={`bg-gradient-to-b from-[#f8f8f8] via-[#fafafa] to-white ${photoPageContent.hero_image_url ? 'pt-16' : 'pt-28'} pb-16 md:pb-24`}>
        {/* Gallery Header */}
        <Reveal className="text-center mb-12 md:mb-16">
          <p className="text-[10px] tracking-[0.4em] uppercase text-black/30 mb-2">Gallery</p>
          <h2 className="font-presti text-3xl md:text-4xl text-black/80">Collections</h2>
        </Reveal>
        
        {/* Desktop: Grid Layout */}
        <div className="hidden md:block max-w-7xl mx-auto px-8 lg:px-12">
          <Stagger className="grid grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12" stagger={0.1}>
            {collections.length > 0 ? (
              collections.map((collection, index) => {
                // Calculate shadow based on settings
                const shadowStyle = photoSettings.shadow_enabled ? {
                  boxShadow: `0 ${photoSettings.shadow_blur * 0.5}px ${photoSettings.shadow_blur}px -${photoSettings.shadow_blur * 0.3}px rgba(0,0,0,${photoSettings.shadow_strength / 100 * 0.4})`
                } : {};
                
                return (
                  <StaggerItem key={collection.id} y={40}>
                  <Link 
                    to={`/collection/${collection.id}`}
                    className={`group block ${photoSettings.spotlight_enabled ? 'spotlight-container' : ''}`}
                  >
                    {/* Frame Container */}
                    <div 
                      className="gallery-frame"
                      style={{
                        ...(photoSettings.frame_enabled ? {
                          padding: `${photoSettings.frame_stroke_width * 3}px`,
                          backgroundColor: photoSettings.frame_color,
                        } : {}),
                        ...shadowStyle
                      }}
                    >
                      {/* Image */}
                      <div className="relative aspect-[3/4] overflow-hidden bg-[#f0f0f0]">
                        <img
                          src={collection.coverImage}
                          alt={`${collection.couple} — ${collection.location || ''} wedding photography by Crystal Media`}
                          className="w-full h-full object-cover transition-transform duration-[1200ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.04]"
                          loading={index < 3 ? 'eager' : 'lazy'}
                          decoding="async"
                          style={{ 
                            objectPosition: `${collection.coverFocalPoint?.x || 50}% ${collection.coverFocalPoint?.y || 50}%` 
                          }}
                        />
                      </div>
                    </div>
                    
                    {/* Caption */}
                    <div className="text-center mt-5">
                      <h3 className="font-presti text-lg md:text-xl text-black/80 mb-1 group-hover:text-black transition-colors">
                        {collection.couple}
                      </h3>
                      <p className="text-[9px] tracking-[0.2em] uppercase text-black/40">
                        {collection.location}
                      </p>
                    </div>
                  </Link>
                  </StaggerItem>
                );
              })
            ) : (
              <div className="col-span-full text-center py-20">
                <p className="text-black/30 text-sm tracking-widest uppercase">No collections available</p>
              </div>
            )}
          </Stagger>
          
          {/* View All Collections Button - Desktop */}
          {collections.length > 0 && (
            <div className="text-center mt-16">
              <Link
                to="/collections"
                data-testid="view-all-collections-btn"
                className="inline-flex items-center gap-3 px-10 py-4 border border-black/20 text-black text-xs tracking-[0.2em] uppercase hover:bg-black hover:text-white transition-all duration-300"
              >
                <span>View All Collections</span>
                <ArrowRight size={14} />
              </Link>
            </div>
          )}
        </div>
        
        {/* Mobile: Horizontal Scroll */}
        <div className="md:hidden">
          {/* Scroll Indicator */}
          <div className="flex items-center justify-center gap-2 mb-4 text-black/40">
            <div className="w-6 h-px bg-black/20" />
            <span className="text-[10px] tracking-[0.15em] uppercase scroll-hint-text">Swipe to explore</span>
            <ArrowRight size={12} className="scroll-hint-arrow" />
          </div>
          
          <div className="overflow-x-auto scrollbar-hide">
            <div className="flex gap-6 px-6 pb-4" style={{ minWidth: 'max-content' }}>
              {collections.length > 0 ? (
                collections.map((collection) => {
                  const shadowStyle = photoSettings.shadow_enabled ? {
                    boxShadow: `0 ${photoSettings.shadow_blur * 0.4}px ${photoSettings.shadow_blur * 0.8}px -${photoSettings.shadow_blur * 0.2}px rgba(0,0,0,${photoSettings.shadow_strength / 100 * 0.35})`
                  } : {};
                  
                  return (
                    <Link 
                      key={collection.id}
                      to={`/collection/${collection.id}`}
                      className="flex-shrink-0 group"
                    >
                      {/* Frame Container */}
                      <div 
                        className="gallery-frame"
                        style={{
                          ...(photoSettings.frame_enabled ? {
                            padding: `${photoSettings.frame_stroke_width * 2}px`,
                            backgroundColor: photoSettings.frame_color,
                          } : {}),
                          ...shadowStyle
                        }}
                      >
                        <div className="relative w-[200px] aspect-[3/4] overflow-hidden bg-[#f0f0f0]">
                          <img
                            src={collection.coverImage}
                            alt={`${collection.couple} — ${collection.location || ''} wedding photography by Crystal Media`}
                            className="w-full h-full object-cover"
                            loading="lazy"
                            decoding="async"
                            style={{ 
                              objectPosition: `${collection.coverFocalPoint?.x || 50}% ${collection.coverFocalPoint?.y || 50}%` 
                            }}
                          />
                        </div>
                      </div>
                      
                      {/* Caption */}
                      <div className="text-center mt-4">
                        <h3 className="font-presti text-base text-black/80">
                          {collection.couple}
                        </h3>
                        <p className="text-[9px] tracking-[0.2em] uppercase text-black/40">
                          {collection.location}
                        </p>
                      </div>
                    </Link>
                  );
                })
              ) : (
                <div className="w-full text-center py-12">
                  <p className="text-black/30 text-sm">No collections</p>
                </div>
              )}
            </div>
          </div>
          
          {/* View All Collections Button - Mobile */}
          {collections.length > 0 && (
            <div className="text-center mt-8 px-6">
              <Link
                to="/collections"
                data-testid="view-all-collections-mobile-btn"
                className="inline-flex items-center gap-2 text-xs tracking-[0.15em] uppercase text-black/60 hover:text-black transition-colors"
              >
                <span>View All Collections</span>
                <ArrowRight size={12} />
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* ─── Content Sections ─── */}
      {photoPageContent.sections_enabled !== false && photoPageContent.sections && photoPageContent.sections.length > 0 && (
        <PhotoContentSections sections={photoPageContent.sections} />
      )}

      {/* Tagline/Philosophy Section */}
      {editorial.tagline?.is_active !== false && (
        <section className="py-32 lg:py-48 px-8 lg:px-16 relative section-artistic artistic-border">
          {/* Decorative elements */}
          <div className="absolute top-20 left-10 w-40 h-40 bg-black/[0.02] rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-10 w-60 h-60 bg-black/[0.015] rounded-full blur-3xl" />
          
          <div className="max-w-7xl mx-auto relative">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
              <div 
                id="tagline-visual"
                data-animate
                className={`lg:col-span-5 transition-all duration-1000 ${
                  isVisible('tagline-visual') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
                }`}
              >
                {editorial.tagline?.gifs?.[0]?.url ? (
                  <div className="artistic-shadow overflow-hidden" style={{ aspectRatio: editorial.tagline.gifs[0].aspect_ratio || '4/5' }}>
                    <img 
                      src={editorial.tagline.gifs[0].url} 
                      alt="Featured" 
                      className="w-full h-full object-cover"
                    />
                  </div>
                ) : (
                  <div className="aspect-[4/5] bg-gradient-to-br from-gray-100 to-gray-50 artistic-shadow" />
                )}
              </div>
              
              <div 
                id="tagline"
                data-animate
                className={`lg:col-span-7 lg:pl-12 transition-all duration-1000 delay-200 ${
                  isVisible('tagline') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
                }`}
              >
                <p className="text-[0.6rem] tracking-[0.3em] uppercase text-black/30 mb-8">
                  {editorial.tagline?.subtitle || 'Our Philosophy'}
                </p>
                <p className="font-presti text-3xl md:text-4xl lg:text-5xl text-black/90 leading-[1.3] mb-10">
                  {editorial.tagline?.title || settings?.tagline || 'Where moments become memories'}
                </p>
                <div className="w-20 h-px bg-gradient-to-r from-black/20 to-transparent mb-10" />
                <p className={`text-black/50 text-lg lg:text-xl max-w-xl leading-relaxed italic font-desc-${descriptionFont} mb-10`}>
                  {editorial.tagline?.description || settings?.tagline_description || 'We capture the authentic emotion and beauty of your celebration through cinematic films and timeless photography.'}
                </p>
                {/* About Us Button */}
                <Link
                  to="/about"
                  data-testid="about-us-philosophy-btn"
                  className="inline-flex items-center gap-3 text-xs tracking-[0.2em] uppercase text-black/60 hover:text-black transition-colors border-b border-black/20 hover:border-black pb-2"
                >
                  <span>About Us</span>
                  <ArrowRight size={14} />
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-32 lg:py-48 px-8 lg:px-16 relative section-artistic overflow-hidden" data-testid="photo-cta-section">
        {/* Background Image */}
        {photoPageContent.cta_bg_image_url && (
          <>
            <img
              src={photoPageContent.cta_bg_image_url}
              alt=""
              className="absolute inset-0 w-full h-full object-cover"
              style={{
                objectPosition: (() => {
                  const fp = isMobile
                    ? (photoPageContent.cta_focal_mobile || { x: 50, y: 50 })
                    : (photoPageContent.cta_focal_desktop || { x: 50, y: 50 });
                  return `${fp.x}% ${fp.y}%`;
                })()
              }}
            />
            <div
              className="absolute inset-0"
              style={{
                backgroundColor: photoPageContent.cta_overlay_color,
                opacity: photoPageContent.cta_overlay_opacity,
              }}
            />
          </>
        )}
        {/* Decorative gradient orbs */}
        {!photoPageContent.cta_bg_image_url && (
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-radial from-black/[0.02] to-transparent rounded-full blur-2xl" />
        )}
        
        <div className="max-w-4xl mx-auto text-center relative">
          <p className={`text-[0.6rem] tracking-[0.3em] uppercase mb-8 ${photoPageContent.cta_bg_image_url ? 'text-white/60' : 'text-black/30'}`}>Let&apos;s Create</p>
          <h2 className={`font-presti text-4xl md:text-5xl lg:text-6xl leading-[1.1] mb-10 ${photoPageContent.cta_bg_image_url ? 'text-white' : 'text-black/90'}`}>
            Ready to capture your story?
          </h2>
          <p className={`mb-12 max-w-lg mx-auto leading-relaxed text-lg italic font-desc-${descriptionFont} ${photoPageContent.cta_bg_image_url ? 'text-white/70' : 'text-black/50'}`}>
            We accept a limited number of clients each year to ensure every experience receives our full focus.
          </p>
          <Link
            to="/inquire"
            className={`inline-flex items-center gap-4 px-12 py-5 text-xs tracking-[0.2em] uppercase artistic-shadow hover:scale-[1.02] transition-all ${
              photoPageContent.cta_bg_image_url
                ? 'bg-white text-black hover:bg-white/90'
                : 'bg-black text-white hover:bg-black/90'
            }`}
          >
            <span>Inquire Now</span>
            <ArrowRight size={14} />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-16 px-8 border-t border-black/5">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-8">
          {logoUrl && !logoError ? (
            <img 
              src={logoUrl} 
              alt="Crystal Media" 
              className="h-8 w-auto opacity-60"
              onError={() => setLogoError(true)}
            />
          ) : (
            <span className="font-presti text-sm tracking-[0.2em] text-black/60">CRYSTAL MEDIA</span>
          )}
          <p
            onClick={(e) => {
              if (e.detail === 3) setShowLoginModal(true);
            }}
            onTouchStart={(e) => {
              const el = e.currentTarget;
              el._lpTimer = setTimeout(() => {
                el._lpFired = true;
                setShowLoginModal(true);
                if (navigator.vibrate) { try { navigator.vibrate(30); } catch (_) {} }
              }, 1200);
            }}
            onTouchEnd={(e) => { if (e.currentTarget._lpTimer) clearTimeout(e.currentTarget._lpTimer); }}
            onTouchMove={(e) => { if (e.currentTarget._lpTimer) clearTimeout(e.currentTarget._lpTimer); }}
            onTouchCancel={(e) => { if (e.currentTarget._lpTimer) clearTimeout(e.currentTarget._lpTimer); }}
            onContextMenu={(e) => e.preventDefault()}
            data-testid="footer-copyright-photo"
            className="text-[10px] tracking-[0.2em] uppercase text-black/30 cursor-pointer select-none hover:text-black/50 transition-colors"
          >
            © {new Date().getFullYear()} Crystal Media. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            <Link to="/crystal-difference" className="text-[10px] tracking-[0.2em] uppercase text-black/30 hover:text-black transition-colors">
              Why Us
            </Link>
            <Link to="/film" className="text-[10px] tracking-[0.2em] uppercase text-black/30 hover:text-black transition-colors">
              Film
            </Link>
            <Link to="/services" className="text-[10px] tracking-[0.2em] uppercase text-black/30 hover:text-black transition-colors">
              Services
            </Link>
            <Link to="/about" className="text-[10px] tracking-[0.2em] uppercase text-black/30 hover:text-black transition-colors">
              About
            </Link>
            <Link to="/inquire" className="text-[10px] tracking-[0.2em] uppercase text-black/30 hover:text-black transition-colors">
              Contact
            </Link>
          </div>
        </div>
      </footer>
      <AdminLoginModal isOpen={showLoginModal} onClose={() => setShowLoginModal(false)} />
    </main>
  );
};

export default PhotoPage;
